/* eslint-disable indent */
import '../css/typography.css';

import React from 'react';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import styled from 'styled-components';
import CarvanaFooter from '@carvana/footer';
import CarvanaHeader, { CarvanaHeaderProvider, CarvanaHeaderContext } from '@carvana/header';
import ThemeProvider, {
  createGlobalStyle,
  CarvanaThemeDeprecated,
  CarvanaTheme
} from '@carvana/showroom-theme';
import { MicroFEShell, MicroFEComponent } from '@carvana/microfrontend';
import { createClient, Provider as UrqlProvider, dedupExchange, fetchExchange } from 'urql';
import fetch from 'isomorphic-fetch';
import { Provider as AnalyticsProvider } from '@carvana/analytics';
import { BreakpointProvider } from '../helpers/breakpoint-provider';
import { SupportCenterProvider } from '../helpers/context-provider';
import { getCookie } from '../utils/get-cookie';
import { AnalyticConfig } from '../utils/analytics-config';
import { OnSiteUnload } from '../helpers/send-in-view-event';
import { ErrorBoundary } from '../helpers/error-boundary';

const env = process.env.GATSBY_ACTIVE_ENV === 'prod' ? 'production' : 'test';
const GlobalStyles = createGlobalStyle(env || 'test');

const Wrapper = styled.main`
  margin: 0 auto;
  box-shadow: rgba(24, 53, 88, 0.1) 1px 2px 3px 1px;
  min-width: 375px;
  max-width: 1800px;
`;

const Layout = ({ children }) => {
  OnSiteUnload({ AnalyticConfig });
  const client = createClient({
    url: process.env.GATSBY_NODE_API_URL,
    fetch,
    fetchOptions: {
      headers: {
        browsercookieid: getCookie('BrowserCookieId'),
        cvaccesstoken: getCookie('CVAccessToken')
      },
      credentials: 'same-origin'
    },
    exchanges: [
      dedupExchange, // dedupe first so we don’t do unnecessary work
      fetchExchange // async, so it needs to go last
    ]
  });
  const config = {
    globals: {
      React: () => import('react'),
      ReactDOM: () => import('react-dom'),
      'react-dom': () => import('react-dom'),
      styled: () => import('styled-components'),
      'styled-components': () => import('styled-components'),
      'prop-types': () => import('prop-types'),
      PropTypes: () => import('prop-types')
    },
    files: [
      [
        'HeaderModule',
        [
          {
            component: 'default',
            core: true,
            default: {
              file: 'index.js',
              manifest: `${
                process.env.GATSBY_CDN_BASE_URL
              }/header/module/assets-manifest.json?v=${Date.now()}`
            },
            ssr: CarvanaHeader
          }
        ]
      ],
      [
        'FooterModule',
        [
          {
            component: 'default',
            core: true,
            local: {
              file: () => import('@carvana/footer')
            },
            default: {
              file: 'index.js',
              manifest: `${
                process.env.GATSBY_CDN_BASE_URL
              }/footer/module/assets-manifest.json?v=${Date.now()}`
            },
            ssr: CarvanaFooter
          }
        ]
      ]
    ]
  };

  return (
    <UrqlProvider value={client}>
      <CarvanaHeaderProvider>
        <Helmet>
          <meta
            name="viewport"
            content="width=device-width, initial-scale=1, shrink-to-fit=no, user-scalable=no"
          />
        </Helmet>
        <ThemeProvider theme={{ ...CarvanaTheme, ...CarvanaThemeDeprecated }}>
          <BreakpointProvider>
            <GlobalStyles />
            <MicroFEShell config={config} env={env} name="support-hub" ssr>
              <CarvanaHeaderContext.Consumer>
                {(headerContext) => (
                  <MicroFEComponent
                    id="default"
                    module="HeaderModule"
                    environment={env}
                    chatContext="carvana_supporthub"
                    useProvider
                    headerContext={headerContext}
                    shouldLoadPlugin={false}
                  />
                )}
              </CarvanaHeaderContext.Consumer>

              <AnalyticsProvider configs={AnalyticConfig}>
                <SupportCenterProvider AnalyticConfig={AnalyticConfig}>
                  <ErrorBoundary AnalyticConfig={AnalyticConfig}>
                    <Wrapper>{children}</Wrapper>
                  </ErrorBoundary>
                </SupportCenterProvider>
              </AnalyticsProvider>

              <MicroFEComponent
                id="default"
                module="FooterModule"
                environment={env}
                withoutProvider
              />
            </MicroFEShell>
          </BreakpointProvider>
        </ThemeProvider>
      </CarvanaHeaderProvider>
    </UrqlProvider>
  );
};

Layout.propTypes = {
  children: PropTypes.node.isRequired
};

export default Layout;
